/**
 * CopilotPanel — the long-lived right-side panel mounted in `_layout.tsx`.
 *
 * Distinct from `<Sheet>` (which is single-shot / modal). The Copilot
 * panel persists across navigations, has its own header chrome
 * (breadcrumb chip + collapse + history toggle), and does **not** dim
 * the main content — on `lg+` you can still click into the dashboard
 * editor or process graph while the panel is open. That's the whole
 * point: changes the user accepts in the panel render immediately in
 * the page behind it.
 *
 * Width: 380px default, 420px at xl. Slides in from the right. Hidden
 * when `isOpen === false`. `Cmd/Ctrl+J` toggles open from anywhere.
 *
 * Where the AI Casting Consultant is open (lib/copilot/availability), the panel is the consultant's conversation itself
 * (next phase, step 3, 2026-10-06): a card's "Ask", the chip over a selected passage and the top bar's button open it
 * with a question drafted and what it is about (store.ask), and the person stays on the page they asked from. The
 * selection chip lives here, too, so it works with the panel closed (SelectionAsk). Elsewhere the panel is the Phase-0
 * chat, as before.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronRight,
  Clock,
  History,
  PanelRightClose,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Badge } from "@/lib/ui/badge";
import { cn } from "@/lib/ui/cn";
import { useCopilot } from "@/lib/copilot/store";
import { nextPanelAsk, type CopilotAskNumbered, type PanelAskState } from "@/lib/copilot/panel-state";
import { COPILOT_COMING_SOON, COPILOT_COMING_SOON_LABEL } from "@/lib/copilot/flags";
import { CONSULTANT_PATH, edgeTabShows, panelContent } from "@/lib/copilot/availability";
import { isPlainClick, launcherAction } from "@/lib/copilot/ask";
import { ConsultantEdgeTab } from "./ConsultantEdgeTab";
import { useCopilotAvailability } from "@/lib/api/copilot-imports";
import { useBriefListQuery, usePlantBriefQuery } from "@/lib/api/brief";
import { quickAsksOf } from "@/lib/brief/ask";
import { plantStarters, quickAsksBeside } from "@/lib/copilot/starters";
import { canAskTheBrain } from "@/lib/imports/copilot";
import { useOrg } from "@/lib/org-context";
import { ImportsConversation } from "@/routes/twin/imports-copilot";
import { CopilotChat } from "./CopilotChat";
import { CopilotHistoryDrawer } from "./CopilotHistoryDrawer";
import { SelectionAsk } from "./SelectionAsk";
import { LAUNCHER_ATTR, PANEL_ATTR, focusIsFree, type ElementLike } from "@/lib/copilot/selection";
import { PANEL_WIDTH } from "@/lib/copilot/panel-layout";

/**
 * Where the AI Casting Consultant is open (lib/copilot/availability), the panel, which a person may have left open or
 * opens with Cmd/Ctrl+J, points to its page instead of the Phase-0 chat (2026-09-26).
 */
export function ConsultantMoved({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="flex flex-col gap-2 p-4 text-sm text-on-surface">
      <p className="font-semibold">The AI Casting Consultant has its own page.</p>
      <p className="text-on-surface-muted">
        It answers from this organisation&apos;s records, asks back when it needs to, and proposes changes you approve.
      </p>
      <Link to={CONSULTANT_PATH} onClick={onOpen} className="font-medium text-brand hover:underline">
        Open the consultant
      </Link>
    </div>
  );
}

/**
 * The panel's header where the consultant is open: its name, a link to its full page (which closes the panel), and the
 * collapse button. `page` is false on the consultant's own page, where the link would lead nowhere.
 */
export function ConsultantPanelHeader({ onClose, page = true }: { onClose: () => void; page?: boolean }) {
  return (
    <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground">
        <Sparkles className="size-3.5" />
      </div>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-on-surface">AI Casting Consultant</span>
      {page && (
        <Link
          to={CONSULTANT_PATH}
          // A click that opens the page in another tab leaves the panel as it is.
          onClick={(e) => {
            if (isPlainClick(e)) onClose();
          }}
          className="shrink-0 rounded-sm text-xs font-medium text-on-surface-muted underline underline-offset-2 hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Open the full page
        </Link>
      )}
      <Button size="icon" variant="ghost" aria-label="Collapse AI Casting Consultant" onClick={onClose}>
        <PanelRightClose className="size-4" />
      </Button>
    </header>
  );
}

/** On the consultant's own page, which shows the conversation: the panel adds none beside it. */
export function ConsultantHere() {
  return (
    <p className="p-4 text-sm text-on-surface-muted">
      This page is the consultant&apos;s conversation, so the panel has nothing to add. Ask about another page&apos;s
      cards and passages and it opens here.
    </p>
  );
}

/**
 * The consultant's conversation in the panel: the thread the page shows, filling the panel and scrolling inside it.
 * A request (store.ask) drafts its question and sets its context; each request has a number of its own, so a new ask
 * starts afresh (ImportsConversation opens a new thread for a draft or a context). The starters are the plant's own,
 * from its latest brief and its day briefs (plantStarters: two generic ones without a brief), each sent as the person's
 * question when tapped; the quick questions are the consultant page's, from the same cached brief, less any a starter
 * already asks.
 */
function PanelConversation({ request }: { request: CopilotAskNumbered | null }) {
  const { role } = useOrg();
  const { isOpen } = useCopilot();
  const brief = usePlantBriefQuery();
  // The day briefs' list (the Briefs page's cached read) is asked for once the panel opens, never on every page.
  const days = useBriefListQuery("day", isOpen);
  const starters = useMemo(() => plantStarters(brief.data, days.data?.items), [brief.data, days.data]);
  const quickAsks = useMemo(() => quickAsksBeside(quickAsksOf(brief.data), starters), [brief.data, starters]);
  // The ask the conversation shows stays until a new one comes (closing the panel forgets the request, never the
  // conversation); a new one waits while the person has a message unsent (#1186 review MEDIUM 2).
  const [asks, setAsks] = useState<PanelAskState>({ shown: request, pending: null });
  const [hasDraft, setHasDraft] = useState(false);
  useEffect(() => {
    setAsks((state) => nextPanelAsk(state, request, hasDraft));
    // A new request alone moves it; the draft's state is read as it is when one comes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);
  const { shown, pending } = asks;
  return (
    <div className="flex h-full min-h-0 flex-col">
      {pending && (
        <div role="alert" data-panel-pending-ask="" className="flex flex-col gap-2 border-b border-border bg-warning/10 px-3 py-2 text-xs text-on-surface">
          <p>You have a message you have not sent. Start the new question instead?</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                setAsks({ shown: pending, pending: null });
                setHasDraft(false);
              }}
            >
              Start the new question
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAsks({ shown, pending: null })}>
              Keep my message
            </Button>
          </div>
        </div>
      )}
      <div className="min-h-0 flex-1">
        <ImportsConversation
          key={shown?.nonce ?? 0}
          canAsk={canAskTheBrain(role)}
          heading={false}
          draft={shown?.draft ?? ""}
          context={shown?.about ?? null}
          quickAsks={quickAsks}
          starters={starters}
          contained
          onDraftChange={setHasDraft}
        />
      </div>
    </div>
  );
}

export function CopilotPanel() {
  const copilot = useCopilot();
  const { pathname } = useLocation();
  const [draft, setDraft] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const content = panelContent(useCopilotAvailability(), pathname);

  // Global Cmd/Ctrl+J toggles the panel.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        copilot.toggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [copilot]);

  // Collapsing the panel gives the focus back to the button that opens it, where the panel had it (or the browser
  // left it on the page's body as the panel hid); a focus the person moved elsewhere stays (#1186 review LOW 3).
  const wasOpen = useRef(copilot.isOpen);
  useEffect(() => {
    if (wasOpen.current && !copilot.isOpen && focusIsFree(document.activeElement as ElementLike | null)) {
      document.querySelector<HTMLElement>(`[${LAUNCHER_ATTR}]`)?.focus();
    }
    wasOpen.current = copilot.isOpen;
  }, [copilot.isOpen]);
  // Escape anywhere in the panel collapses it, unless something inside took the key first (a menu, a dialog). Heard on
  // the window as it bubbles, so what is inside answers first.
  useEffect(() => {
    if (!copilot.isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (!(e.target instanceof Element) || !e.target.closest(`[${PANEL_ATTR}]`)) return;
      e.preventDefault();
      copilot.close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [copilot]);

  // The chip over a selected passage lives with the panel, open or not, only where the consultant answers; so does the
  // tab at the screen's edge, which opens the panel as the top bar's button does (the page as context, nothing drafted).
  const chip = (
    <>
      {content === "conversation" ? <SelectionAsk /> : null}
      {edgeTabShows(content, copilot.isOpen) ? (
        <ConsultantEdgeTab
          onOpen={() => {
            // The top bar's own rule (launcherAction): where the tab shows, that is an ask with the page as context.
            const action = launcherAction(content, copilot.isOpen, copilot.pageContext, pathname);
            if (action.kind === "ask") copilot.ask(action.request);
          }}
        />
      ) : null}
    </>
  );
  // The chip and the panel always stand in the same two places, so opening the panel never starts the chip over.
  const withChip = (aside: ReactNode) => (
    <>
      {chip}
      {aside}
    </>
  );
  const frame = cn(
    "fixed inset-y-0 right-0 top-14 z-30 flex w-full flex-col border-l border-border bg-surface-elevated shadow-xl",
    "transition-transform",
    PANEL_WIDTH,
  );

  // Where the consultant answers, the conversation stays mounted while the panel is closed, hidden, so collapsing the
  // panel never loses what the person typed (#1186 review MEDIUM 2).
  if (content === "conversation") {
    return withChip(
      <aside
        role="complementary"
        aria-label="AI Casting Consultant"
        data-consultant-panel=""
        hidden={!copilot.isOpen}
        className={cn(frame, !copilot.isOpen && "hidden")}
      >
        <ConsultantPanelHeader onClose={copilot.close} page />
        <div className="min-h-0 flex-1">
          <PanelConversation request={copilot.request} />
        </div>
      </aside>,
    );
  }

  if (!copilot.isOpen) return withChip(null);

  if (content === "here") {
    return withChip(
      <aside role="complementary" aria-label="AI Casting Consultant" data-consultant-panel="" className={frame}>
        <ConsultantPanelHeader onClose={copilot.close} page={false} />
        <div className="min-h-0 flex-1">
          <ConsultantHere />
        </div>
      </aside>,
    );
  }

  if (content === "moved") {
    return withChip(
      <aside role="complementary" aria-label="AI Casting Consultant" data-consultant-panel="" className={frame}>
        <header className="flex items-center gap-2 border-b border-border px-3 py-2">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground">
            <Sparkles className="size-3.5" />
          </div>
          <span className="flex-1 text-sm font-semibold text-on-surface">AI Casting Consultant</span>
          <Button size="icon" variant="ghost" aria-label="Collapse AI Casting Consultant" onClick={copilot.close}>
            <PanelRightClose className="size-4" />
          </Button>
        </header>
        <ConsultantMoved onOpen={copilot.close} />
      </aside>,
    );
  }

  return withChip(
    <aside role="complementary" aria-label="AI Casting Consultant" data-consultant-panel="" className={frame}>
      {/* Header */}
      <header className="flex items-center gap-2 border-b border-border px-3 py-2">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground">
          <Sparkles className="size-3.5" />
        </div>
        <div className="flex flex-1 flex-col leading-tight">
          <span className="text-sm font-semibold text-on-surface">AI Casting Consultant</span>
          {copilot.pageContext && (
            <span className="flex items-center gap-1 text-[10px] text-on-surface-muted">
              <ChevronRight className="size-3" />@ {copilot.pageContext.label}
            </span>
          )}
        </div>
        <Badge variant="warning" className="hidden text-[9px] sm:inline-flex">
          {COPILOT_COMING_SOON ? COPILOT_COMING_SOON_LABEL : "Phase 0"}
        </Badge>
        <Button
          size="icon"
          variant="ghost"
          aria-label="History"
          title="Audit history"
          onClick={() => setHistoryOpen((v) => !v)}
        >
          <History className="size-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          aria-label="Clear thread"
          title="Clear conversation"
          onClick={() => {
            if (window.confirm("Clear the AI Casting Consultant conversation? This can't be undone.")) {
              copilot.clearThread();
            }
          }}
        >
          <Trash2 className="size-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          aria-label="Collapse AI Casting Consultant"
          onClick={copilot.close}
        >
          <PanelRightClose className="size-4" />
        </Button>
      </header>

      {/* History drawer (collapsible above the chat) */}
      {historyOpen && (
        <div className="max-h-[40%] overflow-y-auto border-b border-border">
          <CopilotHistoryDrawer onClose={() => setHistoryOpen(false)} />
        </div>
      )}

      {/* Demo gating (#117): coming-soon notice. The thread stays visible
          for context, but the input is disabled inside CopilotChat. */}
      {COPILOT_COMING_SOON && (
        <div className="flex items-start gap-2 border-b border-border bg-warning/10 px-3 py-2 text-[11px] text-on-surface">
          <Clock className="mt-0.5 size-3.5 shrink-0 text-warning" />
          <p>
            <span className="font-semibold">AI Casting Consultant is coming soon.</span> The
            grounded model-authoring and investigation assistant is not open
            for this organisation yet, so chat is disabled for now.
          </p>
        </div>
      )}

      {/* Chat surface */}
      <div className="min-h-0 flex-1">
        <CopilotChat draft={draft} onDraftChange={setDraft} mode="panel" />
      </div>

      {/* Footer link to full-page authoring view (kept for the model-authoring flow) */}
      <div className="border-t border-border px-3 py-1.5">
        <Link
          to="/app/twin/copilot?ctx=model_authoring:plant"
          className="text-[10px] text-on-surface-muted hover:text-brand"
          onClick={() => copilot.close()}
        >
          Open model-authoring view →
        </Link>
      </div>
    </aside>,
  );
}
